import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Spinner } from '@app/ui'
import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { AppLayout } from '@/components/AppLayout'
import { LoginPage } from '@/pages/LoginPage'

/**
 * Load a page's code the first time its route is visited, so the first screen
 * (sign-in) doesn't wait for the whole app. Vite emits one chunk per page.
 */
function page<K extends string>(
  load: () => Promise<{ [P in K]: ComponentType }>,
  name: K,
): () => Promise<{ Component: ComponentType }> {
  return async () => ({ Component: (await load())[name] })
}

/** Shown while the first page's code downloads (a slow connection). */
function RouteLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center gap-2 bg-ds-app text-ds-sm text-ds-muted">
      <Spinner size="sm" label="Loading" />
      Loading…
    </div>
  )
}

/**
 * Application routes.
 * Public: `/login`, `/invite`, `/forgot-password`, `/reset-password`
 * Protected: app shell with companies, loans, admin tools
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/invite',
    hydrateFallbackElement: <RouteLoading />,
    lazy: page(() => import('@/pages/AcceptInvitePage'), 'AcceptInvitePage'),
  },
  {
    path: '/forgot-password',
    hydrateFallbackElement: <RouteLoading />,
    lazy: page(() => import('@/pages/ForgotPasswordPage'), 'ForgotPasswordPage'),
  },
  {
    path: '/reset-password',
    hydrateFallbackElement: <RouteLoading />,
    lazy: page(() => import('@/pages/ResetPasswordPage'), 'ResetPasswordPage'),
  },
  {
    element: <ProtectedRoute />,
    hydrateFallbackElement: <RouteLoading />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/loans" replace /> },
          { path: 'loans', lazy: page(() => import('@/pages/LoansPage'), 'LoansPage') },
          {
            path: 'loans/:id',
            lazy: page(() => import('@/pages/LoanWorkspaceLayout'), 'LoanWorkspaceLayout'),
            children: [
              { index: true, lazy: page(() => import('@/pages/LoanDetailPage'), 'LoanDetailPage') },
              {
                path: 'borrowers/:borrowerId',
                lazy: page(() => import('@/pages/BorrowerDetailPage'), 'BorrowerDetailPage'),
              },
            ],
          },
          { path: 'companies', lazy: page(() => import('@/pages/CompaniesPage'), 'CompaniesPage') },
          {
            path: 'companies/:id',
            lazy: page(() => import('@/pages/CompanyWorkspaceLayout'), 'CompanyWorkspaceLayout'),
            children: [{ index: true, lazy: page(() => import('@/pages/CompanyDetailPage'), 'CompanyDetailPage') }],
          },
          {
            path: 'admin/settings',
            lazy: page(() => import('@/pages/AdminSettingsWorkspaceLayout'), 'AdminSettingsWorkspaceLayout'),
            children: [
              { index: true, lazy: page(() => import('@/pages/PlatformSettingsPage'), 'PlatformSettingsPage') },
              { path: 'users', lazy: page(() => import('@/pages/AdminUsersPage'), 'AdminUsersPage') },
              { path: 'default-fees', lazy: page(() => import('@/pages/PlatformDefaultFeesPage'), 'PlatformDefaultFeesPage') },
              { path: 'counties', lazy: page(() => import('@/pages/CountiesPage'), 'CountiesPage') },
              {
                path: 'income-keywords',
                lazy: page(() => import('@/pages/BankIncomeExclusionKeywordsPage'), 'BankIncomeExclusionKeywordsPage'),
              },
            ],
          },
          {
            path: 'admin/servicers',
            lazy: page(() => import('@/pages/ServicersPage'), 'ServicersPage'),
          },
          {
            path: 'admin/title-companies',
            lazy: page(() => import('@/pages/TitleCompaniesPage'), 'TitleCompaniesPage'),
          },
          {
            path: 'admin/attorneys',
            lazy: page(() => import('@/pages/AttorneysPage'), 'AttorneysPage'),
          },
          {
            path: 'admin/apor',
            lazy: page(() => import('@/pages/AporPage'), 'AporPage'),
          },
          {
            path: 'admin/validation-rules',
            lazy: page(() => import('@/pages/LoanValidationRulesPage'), 'LoanValidationRulesPage'),
          },
          {
            path: 'admin/apor/validation-rules',
            element: (
              <Navigate to="/admin/validation-rules" replace />
            ),
          },
          // Legacy admin paths → Settings secondary sections
          {
            path: 'admin/counties',
            element: <Navigate to="/admin/settings/counties" replace />,
          },
          {
            path: 'admin/income-exclusion-keywords',
            element: (
              <Navigate to="/admin/settings/income-keywords" replace />
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
